class ApiClient {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL;
    console.log('ApiClient initialized with baseURL:', this.baseURL);
  }

  getHeaders(customHeaders = {}) {
    const token = localStorage.getItem('access_token');
    
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    if (customHeaders['Content-Type'] === null) {
      delete headers['Content-Type'];
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No access token found in localStorage');
    }

    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getHeaders(options.headers);

    const config = {
      ...options,
      headers,
      credentials: 'omit', // Don't send cookies since we're using bearer token
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      let detail = response.statusText;
      try {
        const bodyText = await response.text();
        if (bodyText) {
          try {
            const json = JSON.parse(bodyText);
            detail = json.message || json.error || json.msg || bodyText;
          } catch {
            detail = bodyText;
          }
        }
      } catch {
        // keep statusText
      }
      const error = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      error.response = response;
      error.status = response.status;
      throw error;
    }


    // Check if there's content to parse
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    }
    
    // Return null for empty responses (like 204 No Content)
    return null;
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /** Multipart upload (FormData). Do not set Content-Type — browser adds boundary. */
  async postForm(endpoint, formData, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': null, ...(options.headers || {}) },
      body: formData,
    });
  }

  /** Authenticated binary download (e.g. audio). Returns a Blob. */
  async getBlob(endpoint) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getHeaders({ 'Content-Type': null });
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'omit',
    });
    if (!response.ok) {
      const error = new Error(response.statusText);
      error.response = response;
      throw error;
    }
    return response.blob();
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: 'DELETE', headers: { 'Content-Type': null }, ...options });
  }
}

// Export a singleton instance
export const apiClient = new ApiClient(); 